import React, { useState } from 'react';
import { EyeIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { saveAs } from '../utils/saveFile';
import { previewEdiOrderFile, downloadEdiOrderFile, ediFileName } from '../services/ediorders.service';

/**
 * View / Download the raw inbound EDI file for one edi_orders record — the same authenticated
 * blob-fetch pattern the order / repair-order Documents panels use (View opens the blob in a
 * new tab; Download goes through saveAs). Renders nothing when there's no file
 * (nas_edifilename blank), so callers can drop it in unconditionally. Used on the EDI Orders
 * tablemaint drawer + the EDI Info modal, whether or not the order is tied to a pro.
 */
const EdiFileActions: React.FC<{
  recno: number;
  nasEdifilename?: string | null;
  className?: string;
}> = ({ recno, nasEdifilename, className }) => {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'' | 'view' | 'download'>('');
  if (!(nasEdifilename || '').trim()) return null;

  const view = async () => {
    setError(null); setBusy('view');
    try {
      const blob = await previewEdiOrderFile(recno);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError('Could not open the EDI file.');
    } finally { setBusy(''); }
  };

  const download = async () => {
    setError(null); setBusy('download');
    try {
      await saveAs(ediFileName(nasEdifilename || ''), () => downloadEdiOrderFile(recno));
    } catch {
      setError('Could not download the EDI file.');
    } finally { setBusy(''); }
  };

  const btn = 'inline-flex items-center gap-1 rounded border border-gray-300 dark:border-gray-600 px-1.5 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-50';
  return (
    <span className={`inline-flex items-center gap-1.5 ${className || ''}`}>
      <button type="button" onClick={view} disabled={!!busy} className={btn} title="View the raw EDI file">
        <EyeIcon className="w-3.5 h-3.5" /> View
      </button>
      <button type="button" onClick={download} disabled={!!busy} className={btn} title="Download the raw EDI file">
        <ArrowDownTrayIcon className="w-3.5 h-3.5" /> Download
      </button>
      {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
    </span>
  );
};

export default EdiFileActions;
